import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'

import { getCookie } from 'cookies-next'
import { Visibility } from 'globalTypes'

import { gql, useQuery } from '@apollo/client'
import { useLingui } from '@lingui/react'
import { Context as AuthorizationsContext } from 'Root/App/Contexts/Authorizations'
import BuyToShare from 'Root/App/Dialog/BuyToShare'
import ShareVisibility from 'Root/App/Dialog/Share'
import { Context } from 'Root/Contexts/Share'
import { isMatching } from 'lib/breakpoint-helper'
import { getElementType } from 'lib/get-element-type'
import { shareCallback } from 'lib/share-callback'
import { GET_WORKSPACE_BY_ID } from 'lib/use-workspace-from-element'

interface ShareOrCopyProps {
  id: string
  openDialogButton: ReactElement
}

const GET_SHARE_OR_COPY_ELEMENT_VISIBILITY = gql`
  query GetShareOrCopyElementVisibility($id: ID!) {
    element(id: $id) {
      __typename

      ... on Collection {
        id
        visibility
        need_registration
        parent_folder_id
        creator {
          id
        }
      }

      ... on Content {
        id
        visibility
        need_registration
        parent_folder_id
        creator {
          id
        }
      }

      ... on Folder {
        id
        visibility
      }
    }
  }
`

const ShareOrCopy: FC<ShareOrCopyProps> = ({ id, openDialogButton }) => {
  const { i18n } = useLingui()
  const [isDialogOpen, setDialogOpen] = useState(false)
  const shareData = useContext(Context)
  const { setElementId, subscriptionPlanCode, userSubscriptionLoading } =
    useContext(AuthorizationsContext)
  const [canUseNavigatorShare, setCanUseNavigatorShare] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [workspaceRole, setWorkspaceRole] = useState(null)
  const [workspaceId, setWorkspaceId] = useState(null)

  useEffect(() => {
    setWorkspaceRole(getCookie('workspace_role'))
    setWorkspaceId(getCookie('workspace_id') || null)
  }, [])

  useEffect(() => {
    setCanUseNavigatorShare(
      typeof window !== 'undefined' &&
        !!window.navigator.share &&
        !!window.navigator.canShare
    )
    setIsMobile(isMatching('sm', true))
  }, [])

  const { data, loading } = useQuery(GET_SHARE_OR_COPY_ELEMENT_VISIBILITY, {
    variables: {
      id,
    },
    fetchPolicy: 'cache-first',
  })

  const isManager = workspaceRole === 'manager'

  const { data: workspaceData, loading: workspaceLoading } = useQuery(
    GET_WORKSPACE_BY_ID,
    {
      variables: { workspaceId },
      skip: !isManager || !workspaceId,
      fetchPolicy: 'cache-first',
    }
  )

  const isFetchingData = loading || userSubscriptionLoading

  // managers cannot share elements outside a board that still belong to their
  // own workspace, i.e. created before they were created on behalf of the owner
  const isHiddenForManager =
    isManager &&
    (loading ||
      workspaceLoading ||
      (data?.element?.__typename !== 'Folder' &&
        !data?.element?.parent_folder_id &&
        data?.element?.creator?.id !== workspaceData?.workspace?.user?.id))

  const handleOnConfirm = (visibility: Visibility) => {
    if (visibility !== Visibility.mine) {
      setElementId(id)
    }
  }

  const handleCopyOnClick = shareCallback(
    canUseNavigatorShare && isMobile && window.navigator.canShare(shareData),
    shareData,
    i18n
  )

  const handleShareOnClick =
    data?.element?.visibility !== Visibility.mine &&
    (subscriptionPlanCode !== 'essential' || workspaceRole === 'manager')
      ? () => setElementId(id)
      : () => setDialogOpen((isDialogOpen) => !isDialogOpen)

  if (isHiddenForManager) return null

  return (
    <>
      {React.cloneElement(openDialogButton, {
        onClick: shareData.canManage ? handleShareOnClick : handleCopyOnClick,
        disabled: isFetchingData,
      })}

      {subscriptionPlanCode === 'essential' && workspaceRole !== 'manager' ? (
        <BuyToShare
          isOpen={isDialogOpen}
          onClose={() => setDialogOpen((isDialogOpen) => !isDialogOpen)}
        />
      ) : (
        <ShareVisibility
          id={id}
          isOpen={isDialogOpen}
          onClose={() => setDialogOpen((isDialogOpen) => !isDialogOpen)}
          onConfirm={handleOnConfirm}
          elementType={
            getElementType(shareData.type) as 'collection' | 'content'
          }
          visibility={data?.element?.visibility}
          need_registration={data?.element?.need_registration}
        />
      )}
    </>
  )
}

export default ShareOrCopy
